// Biome-specific Assets for TypeTown Zones
// Each zone has unique environmental elements that make it visually distinct

import React from 'react';

// ============================================
// CENTER ZONE ASSETS (Stage 1) - Town Square
// ============================================

export const TownFlower = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 30 30">
    {/* Stem */}
    <path d="M15 30 L15 15" stroke="#4CAF50" strokeWidth="2" />
    {/* Leaves */}
    <ellipse cx="12" cy="22" rx="4" ry="2" fill="#66BB6A" transform="rotate(-30 12 22)" />
    <ellipse cx="18" cy="24" rx="4" ry="2" fill="#66BB6A" transform="rotate(30 18 24)" />
    {/* Petals */}
    <circle cx="15" cy="10" r="4" fill="#FF6B6B" />
    <circle cx="10" cy="12" r="4" fill="#FF8A80" />
    <circle cx="20" cy="12" r="4" fill="#FF8A80" />
    <circle cx="12" cy="7" r="4" fill="#FFAB91" />
    <circle cx="18" cy="7" r="4" fill="#FFAB91" />
    {/* Center */}
    <circle cx="15" cy="10" r="2.5" fill="#FFE082" />
  </svg>
);

export const ParkBench = ({ size = 50 }) => (
  <svg width={size} height={size * 0.6} viewBox="0 0 50 30">
    {/* Bench seat */}
    <rect x="5" y="10" width="40" height="5" fill="#8D6E63" rx="1" />
    {/* Bench back */}
    <rect x="5" y="3" width="40" height="4" fill="#795548" rx="1" />
    {/* Support bars */}
    <rect x="7" y="7" width="2" height="3" fill="#6D4C41" />
    <rect x="41" y="7" width="2" height="3" fill="#6D4C41" />
    {/* Legs */}
    <rect x="8" y="15" width="3" height="15" fill="#5D4037" />
    <rect x="39" y="15" width="3" height="15" fill="#5D4037" />
    {/* Armrests */}
    <rect x="3" y="8" width="4" height="8" fill="#6D4C41" rx="1" />
    <rect x="43" y="8" width="4" height="8" fill="#6D4C41" rx="1" />
  </svg>
);

export const Fountain = ({ size = 60 }) => (
  <svg width={size} height={size} viewBox="0 0 60 60">
    <style>
      {`
        @keyframes water-spray {
          0%, 100% { transform: translateY(0); opacity: 0.8; }
          50% { transform: translateY(-5px); opacity: 0.4; }
        }
        .fountain-water {
          animation: water-spray 1.5s ease-in-out infinite;
        }
      `}
    </style>
    {/* Base pool */}
    <ellipse cx="30" cy="55" rx="28" ry="8" fill="#90CAF9" />
    <ellipse cx="30" cy="52" rx="25" ry="6" fill="#64B5F6" />
    {/* Stone rim */}
    <ellipse cx="30" cy="50" rx="28" ry="6" fill="#BDBDBD" />
    <ellipse cx="30" cy="48" rx="25" ry="5" fill="#90CAF9" />
    {/* Center pillar */}
    <rect x="26" y="25" width="8" height="25" fill="#9E9E9E" />
    {/* Top bowl */}
    <ellipse cx="30" cy="25" rx="12" ry="4" fill="#BDBDBD" />
    <ellipse cx="30" cy="23" rx="10" ry="3" fill="#64B5F6" />
    {/* Water sprays */}
    <g className="fountain-water">
      <path d="M30 20 Q30 5 25 8" fill="none" stroke="#64B5F6" strokeWidth="2" opacity="0.7" />
      <path d="M30 20 Q30 5 35 8" fill="none" stroke="#64B5F6" strokeWidth="2" opacity="0.7" />
      <path d="M30 20 L30 5" stroke="#90CAF9" strokeWidth="2" opacity="0.6" />
    </g>
    {/* Water droplets */}
    <circle cx="25" cy="12" r="1.5" fill="#64B5F6" className="fountain-water" />
    <circle cx="35" cy="10" r="1.5" fill="#64B5F6" className="fountain-water" style={{animationDelay: '0.3s'}} />
    <circle cx="30" cy="8" r="2" fill="#90CAF9" className="fountain-water" style={{animationDelay: '0.5s'}} />
  </svg>
);

export const Lamppost = ({ size = 40 }) => (
  <svg width={size} height={size * 1.5} viewBox="0 0 40 60">
    <style>
      {`
        @keyframes lamp-glow {
          0%, 100% { opacity: 0.8; }
          50% { opacity: 1; }
        }
        .lamp-light {
          animation: lamp-glow 2s ease-in-out infinite;
        }
      `}
    </style>
    {/* Pole */}
    <rect x="18" y="20" width="4" height="40" fill="#37474F" />
    {/* Base */}
    <rect x="14" y="55" width="12" height="5" fill="#455A64" rx="1" />
    {/* Lamp head */}
    <path d="M10 20 L15 10 L25 10 L30 20 Z" fill="#455A64" />
    {/* Glass */}
    <rect x="14" y="12" width="12" height="8" fill="#FFF9C4" className="lamp-light" />
    {/* Top */}
    <path d="M12 10 L20 5 L28 10" fill="#37474F" />
    {/* Glow effect */}
    <ellipse cx="20" cy="16" rx="8" ry="6" fill="#FFEB3B" opacity="0.2" className="lamp-light" />
  </svg>
);

export const Signpost = ({ size = 35 }) => (
  <svg width={size} height={size * 1.3} viewBox="0 0 35 45">
    {/* Pole */}
    <rect x="16" y="15" width="3" height="30" fill="#8D6E63" />
    {/* Base */}
    <ellipse cx="17.5" cy="44" rx="6" ry="2" fill="#6D4C41" />
    {/* Signs pointing different directions */}
    <polygon points="5,8 5,15 25,15 28,11.5 25,8" fill="#FF7043" />
    <polygon points="30,18 30,25 10,25 7,21.5 10,18" fill="#42A5F5" />
    {/* Text lines */}
    <line x1="8" y1="11.5" x2="22" y2="11.5" stroke="#FFFFFF" strokeWidth="1.5" />
    <line x1="13" y1="21.5" x2="27" y2="21.5" stroke="#FFFFFF" strokeWidth="1.5" />
  </svg>
);

export const PicnicTable = ({ size = 55 }) => (
  <svg width={size} height={size * 0.6} viewBox="0 0 55 33">
    {/* Table top */}
    <rect x="5" y="8" width="45" height="4" fill="#A1887F" rx="1" />
    {/* Benches */}
    <rect x="8" y="18" width="15" height="3" fill="#8D6E63" rx="1" />
    <rect x="32" y="18" width="15" height="3" fill="#8D6E63" rx="1" />
    {/* Center support */}
    <polygon points="22,12 27.5,12 32,30 23,30" fill="#795548" />
    {/* Bench legs */}
    <rect x="10" y="21" width="3" height="12" fill="#6D4C41" />
    <rect x="18" y="21" width="3" height="12" fill="#6D4C41" />
    <rect x="34" y="21" width="3" height="12" fill="#6D4C41" />
    <rect x="42" y="21" width="3" height="12" fill="#6D4C41" />
  </svg>
);

export const Mushroom = ({ size = 25 }) => (
  <svg width={size} height={size} viewBox="0 0 25 25">
    {/* Stem */}
    <rect x="9" y="15" width="7" height="10" fill="#F5F5DC" rx="2" />
    {/* Cap */}
    <ellipse cx="12.5" cy="12" rx="11" ry="8" fill="#E53935" />
    {/* Spots */}
    <circle cx="8" cy="10" r="2" fill="#FFFFFF" />
    <circle cx="15" cy="8" r="2.5" fill="#FFFFFF" />
    <circle cx="18" cy="12" r="1.5" fill="#FFFFFF" />
    <circle cx="6" cy="14" r="1.5" fill="#FFFFFF" />
    {/* Cap underside */}
    <ellipse cx="12.5" cy="15" rx="8" ry="2" fill="#FFCDD2" />
  </svg>
);

export const Birdhouse = ({ size = 30 }) => (
  <svg width={size} height={size * 1.2} viewBox="0 0 30 36">
    {/* Pole */}
    <rect x="13" y="25" width="4" height="11" fill="#8D6E63" />
    {/* House body */}
    <rect x="5" y="12" width="20" height="15" fill="#FFCC80" rx="1" />
    {/* Roof */}
    <polygon points="2,12 15,2 28,12" fill="#A1887F" />
    {/* Entrance hole */}
    <circle cx="15" cy="18" r="3" fill="#5D4037" />
    {/* Perch */}
    <rect x="13" y="22" width="4" height="2" fill="#6D4C41" />
    <circle cx="15" cy="24" r="1.5" fill="#8D6E63" />
  </svg>
);

// ============================================
// SOUTHERN BEACH ASSETS (Stage 4)
// ============================================

export const PalmTree = ({ size = 80 }) => (
  <svg width={size} height={size * 1.5} viewBox="0 0 80 120">
    <style>
      {`
        @keyframes palm-sway {
          0%, 100% { transform: rotate(-2deg); }
          50% { transform: rotate(2deg); }
        }
        .palm-fronds {
          transform-origin: 40px 50px;
          animation: palm-sway 4s ease-in-out infinite;
        }
      `}
    </style>
    {/* Trunk */}
    <path d="M38 120 L42 120 L44 50 L36 50 Z" fill="#8B6914" />
    <path d="M36 50 L44 50 L45 45 L35 45 Z" fill="#A0752E" />
    {/* Coconuts */}
    <circle cx="40" cy="48" r="5" fill="#5D4037" />
    <circle cx="34" cy="52" r="4" fill="#5D4037" />
    <circle cx="46" cy="52" r="4" fill="#5D4037" />
    {/* Palm fronds - animated swaying */}
    <g className="palm-fronds">
      <ellipse cx="20" cy="30" rx="25" ry="8" fill="#228B22" transform="rotate(-30 20 30)" />
      <ellipse cx="60" cy="30" rx="25" ry="8" fill="#228B22" transform="rotate(30 60 30)" />
      <ellipse cx="40" cy="15" rx="20" ry="8" fill="#2E8B2E" />
      <ellipse cx="15" cy="45" rx="22" ry="7" fill="#1E7B1E" transform="rotate(-50 15 45)" />
      <ellipse cx="65" cy="45" rx="22" ry="7" fill="#1E7B1E" transform="rotate(50 65 45)" />
      <ellipse cx="30" cy="20" rx="18" ry="6" fill="#32CD32" transform="rotate(-15 30 20)" />
      <ellipse cx="50" cy="20" rx="18" ry="6" fill="#32CD32" transform="rotate(15 50 20)" />
    </g>
  </svg>
);

export const Sandcastle = ({ size = 60 }) => (
  <svg width={size} height={size} viewBox="0 0 60 60">
    {/* Base */}
    <rect x="5" y="40" width="50" height="20" fill="#E8D5A3" rx="2" />
    {/* Towers */}
    <rect x="8" y="25" width="12" height="20" fill="#DEC591" />
    <rect x="40" y="25" width="12" height="20" fill="#DEC591" />
    <rect x="20" y="15" width="20" height="30" fill="#E8D5A3" />
    {/* Tower tops (crenellations) */}
    <rect x="8" y="20" width="4" height="8" fill="#DEC591" />
    <rect x="16" y="20" width="4" height="8" fill="#DEC591" />
    <rect x="40" y="20" width="4" height="8" fill="#DEC591" />
    <rect x="48" y="20" width="4" height="8" fill="#DEC591" />
    <rect x="20" y="10" width="5" height="8" fill="#E8D5A3" />
    <rect x="27" y="10" width="6" height="8" fill="#E8D5A3" />
    <rect x="35" y="10" width="5" height="8" fill="#E8D5A3" />
    {/* Flag */}
    <line x1="30" y1="10" x2="30" y2="0" stroke="#8B4513" strokeWidth="2" />
    <polygon points="30,0 30,8 38,4" fill="#FF6B6B" />
    {/* Door */}
    <path d="M26 45 L26 55 L34 55 L34 45 Q30 40 26 45" fill="#C4A35A" />
    {/* Windows */}
    <rect x="22" y="22" width="4" height="5" fill="#87CEEB" />
    <rect x="34" y="22" width="4" height="5" fill="#87CEEB" />
  </svg>
);

export const BeachUmbrella = ({ size = 50 }) => (
  <svg width={size} height={size} viewBox="0 0 50 50">
    {/* Pole */}
    <line x1="25" y1="20" x2="25" y2="50" stroke="#8B4513" strokeWidth="3" />
    {/* Umbrella top */}
    <path d="M5 22 Q25 -5 45 22 Z" fill="#FF6B6B" />
    <path d="M10 22 Q25 5 40 22" fill="none" stroke="#FFFFFF" strokeWidth="2" />
    <path d="M15 22 Q25 10 35 22" fill="none" stroke="#FFFFFF" strokeWidth="2" />
    {/* Stripes */}
    <path d="M13 20 L25 5 L25 22" fill="#FFE66D" />
    <path d="M37 20 L25 5 L25 22" fill="#FFE66D" />
  </svg>
);

export const Seashell = ({ size = 25 }) => (
  <svg width={size} height={size} viewBox="0 0 25 25">
    <path d="M12.5 2 Q20 5 22 15 Q20 22 12.5 23 Q5 22 3 15 Q5 5 12.5 2" fill="#FFE4C4" />
    <path d="M12.5 5 Q8 8 7 15" fill="none" stroke="#DEB887" strokeWidth="1" />
    <path d="M12.5 5 Q17 8 18 15" fill="none" stroke="#DEB887" strokeWidth="1" />
    <path d="M12.5 5 L12.5 18" stroke="#DEB887" strokeWidth="1" />
    <ellipse cx="12.5" cy="4" rx="3" ry="2" fill="#FFDAB9" />
  </svg>
);

export const BeachBall = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 30 30">
    <circle cx="15" cy="15" r="14" fill="#FF6B6B" />
    <path d="M15 1 Q20 15 15 29" fill="#FFE66D" />
    <path d="M15 1 Q10 15 15 29" fill="#4ECDC4" />
    <path d="M1 15 Q15 10 29 15" fill="#FF6B6B" />
    <circle cx="15" cy="15" r="3" fill="#FFFFFF" opacity="0.5" />
  </svg>
);

export const Crab = ({ size = 25 }) => (
  <svg width={size} height={size * 0.7} viewBox="0 0 25 18">
    {/* Body */}
    <ellipse cx="12.5" cy="12" rx="8" ry="5" fill="#E74C3C" />
    {/* Claws */}
    <ellipse cx="3" cy="8" rx="4" ry="3" fill="#C0392B" />
    <ellipse cx="22" cy="8" rx="4" ry="3" fill="#C0392B" />
    <circle cx="1" cy="6" r="2" fill="#E74C3C" />
    <circle cx="24" cy="6" r="2" fill="#E74C3C" />
    {/* Eyes */}
    <circle cx="10" cy="8" r="2" fill="#2C3E50" />
    <circle cx="15" cy="8" r="2" fill="#2C3E50" />
    <circle cx="10" cy="7" r="1" fill="#FFFFFF" />
    <circle cx="15" cy="7" r="1" fill="#FFFFFF" />
    {/* Legs */}
    <line x1="6" y1="14" x2="2" y2="17" stroke="#C0392B" strokeWidth="1.5" />
    <line x1="8" y1="15" x2="5" y2="18" stroke="#C0392B" strokeWidth="1.5" />
    <line x1="19" y1="14" x2="23" y2="17" stroke="#C0392B" strokeWidth="1.5" />
    <line x1="17" y1="15" x2="20" y2="18" stroke="#C0392B" strokeWidth="1.5" />
  </svg>
);

// ============================================
// SNOWY NORTH ASSETS (Stage 2)
// ============================================

export const Snowman = ({ size = 60 }) => (
  <svg width={size} height={size * 1.2} viewBox="0 0 60 72">
    {/* Bottom ball */}
    <circle cx="30" cy="58" r="14" fill="#FFFFFF" stroke="#E0E0E0" strokeWidth="1" />
    {/* Middle ball */}
    <circle cx="30" cy="38" r="11" fill="#FFFFFF" stroke="#E0E0E0" strokeWidth="1" />
    {/* Head */}
    <circle cx="30" cy="20" r="9" fill="#FFFFFF" stroke="#E0E0E0" strokeWidth="1" />
    {/* Hat */}
    <rect x="22" y="5" width="16" height="10" fill="#2C3E50" />
    <rect x="18" y="13" width="24" height="3" fill="#2C3E50" />
    <rect x="24" y="7" width="4" height="3" fill="#E74C3C" />
    {/* Face */}
    <circle cx="26" cy="18" r="1.5" fill="#2C3E50" />
    <circle cx="34" cy="18" r="1.5" fill="#2C3E50" />
    <path d="M30 20 L33 23 L30 22" fill="#FF7043" />
    <path d="M25 25 Q30 28 35 25" fill="none" stroke="#2C3E50" strokeWidth="1" />
    {/* Buttons */}
    <circle cx="30" cy="33" r="1.5" fill="#2C3E50" />
    <circle cx="30" cy="38" r="1.5" fill="#2C3E50" />
    <circle cx="30" cy="43" r="1.5" fill="#2C3E50" />
    {/* Arms */}
    <line x1="19" y1="35" x2="8" y2="28" stroke="#8B4513" strokeWidth="2" />
    <line x1="41" y1="35" x2="52" y2="28" stroke="#8B4513" strokeWidth="2" />
    {/* Scarf */}
    <path d="M21 27 Q30 30 39 27" fill="none" stroke="#E74C3C" strokeWidth="3" />
    <rect x="36" y="27" width="4" height="12" fill="#E74C3C" rx="1" />
  </svg>
);

export const SnowyPine = ({ size = 80 }) => (
  <svg width={size} height={size * 1.3} viewBox="0 0 80 104">
    {/* Trunk */}
    <rect x="35" y="85" width="10" height="19" fill="#5D4037" />
    {/* Tree layers with snow */}
    <polygon points="40,5 15,40 65,40" fill="#1B5E20" />
    <polygon points="40,0 20,30 60,30" fill="#FFFFFF" opacity="0.8" />
    <polygon points="40,25 10,65 70,65" fill="#2E7D32" />
    <polygon points="40,20 18,55 62,55" fill="#FFFFFF" opacity="0.7" />
    <polygon points="40,45 5,90 75,90" fill="#388E3C" />
    <polygon points="40,40 15,75 65,75" fill="#FFFFFF" opacity="0.6" />
    {/* Snow patches */}
    <ellipse cx="25" cy="55" rx="8" ry="4" fill="#FFFFFF" />
    <ellipse cx="55" cy="55" rx="8" ry="4" fill="#FFFFFF" />
    <ellipse cx="40" cy="75" rx="10" ry="4" fill="#FFFFFF" />
  </svg>
);

export const IceCrystal = ({ size = 35 }) => (
  <svg width={size} height={size} viewBox="0 0 35 35">
    {/* Main crystal shape */}
    <polygon points="17.5,2 22,10 30,10 24,17.5 30,25 22,25 17.5,33 13,25 5,25 11,17.5 5,10 13,10" 
             fill="#B3E5FC" stroke="#81D4FA" strokeWidth="1" />
    {/* Inner shine */}
    <polygon points="17.5,8 20,13 25,13 21,17.5 25,22 20,22 17.5,27 15,22 10,22 14,17.5 10,13 15,13" 
             fill="#E1F5FE" opacity="0.7" />
    {/* Center sparkle */}
    <circle cx="17.5" cy="17.5" r="3" fill="#FFFFFF" />
  </svg>
);

export const Igloo = ({ size = 70 }) => (
  <svg width={size} height={size * 0.7} viewBox="0 0 70 50">
    {/* Main dome */}
    <ellipse cx="40" cy="35" rx="28" ry="20" fill="#F5F5F5" stroke="#E0E0E0" strokeWidth="1" />
    {/* Entrance tunnel */}
    <ellipse cx="10" cy="40" rx="12" ry="10" fill="#F5F5F5" stroke="#E0E0E0" strokeWidth="1" />
    {/* Door opening */}
    <ellipse cx="5" cy="42" rx="5" ry="7" fill="#455A64" />
    {/* Ice block lines */}
    <path d="M20 20 Q40 18 60 25" fill="none" stroke="#E0E0E0" strokeWidth="1" />
    <path d="M15 30 Q40 28 65 32" fill="none" stroke="#E0E0E0" strokeWidth="1" />
    <path d="M14 40 Q35 38 62 42" fill="none" stroke="#E0E0E0" strokeWidth="1" />
    <line x1="30" y1="18" x2="28" y2="45" stroke="#E0E0E0" strokeWidth="1" />
    <line x1="45" y1="17" x2="47" y2="45" stroke="#E0E0E0" strokeWidth="1" />
    <line x1="55" y1="22" x2="58" y2="43" stroke="#E0E0E0" strokeWidth="1" />
  </svg>
);

export const SnowPile = ({ size = 40 }) => (
  <svg width={size} height={size * 0.5} viewBox="0 0 40 20">
    <ellipse cx="20" cy="15" rx="18" ry="8" fill="#FFFFFF" stroke="#E8E8E8" strokeWidth="1" />
    <ellipse cx="12" cy="12" rx="10" ry="6" fill="#FAFAFA" />
    <ellipse cx="28" cy="13" rx="8" ry="5" fill="#F5F5F5" />
    {/* Sparkles */}
    <circle cx="15" cy="10" r="1" fill="#E3F2FD" />
    <circle cx="25" cy="12" r="1" fill="#E3F2FD" />
    <circle cx="20" cy="8" r="0.8" fill="#BBDEFB" />
  </svg>
);

// Animated falling snowflakes for NORTH zone
export const FallingSnow = ({ size = 60 }) => (
  <svg width={size} height={size} viewBox="0 0 60 60">
    <style>
      {`
        @keyframes snowfall-1 {
          0% { transform: translateY(-10px) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(60px) translateX(5px); opacity: 0; }
        }
        @keyframes snowfall-2 {
          0% { transform: translateY(-10px) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(60px) translateX(-5px); opacity: 0; }
        }
        @keyframes snowfall-3 {
          0% { transform: translateY(-10px) translateX(0); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(60px) translateX(3px); opacity: 0; }
        }
        .snowflake-1 { animation: snowfall-1 3s linear infinite; }
        .snowflake-2 { animation: snowfall-2 4s linear infinite; animation-delay: 0.5s; }
        .snowflake-3 { animation: snowfall-3 3.5s linear infinite; animation-delay: 1s; }
        .snowflake-4 { animation: snowfall-1 4.5s linear infinite; animation-delay: 1.5s; }
        .snowflake-5 { animation: snowfall-2 3s linear infinite; animation-delay: 2s; }
        .snowflake-6 { animation: snowfall-3 4s linear infinite; animation-delay: 0.3s; }
      `}
    </style>
    {/* Snowflakes */}
    <circle cx="10" cy="5" r="2" fill="#FFFFFF" className="snowflake-1" />
    <circle cx="25" cy="8" r="3" fill="#F5F5F5" className="snowflake-2" />
    <circle cx="40" cy="3" r="2" fill="#FFFFFF" className="snowflake-3" />
    <circle cx="50" cy="10" r="2.5" fill="#FAFAFA" className="snowflake-4" />
    <circle cx="15" cy="15" r="2" fill="#FFFFFF" className="snowflake-5" />
    <circle cx="35" cy="12" r="2" fill="#F5F5F5" className="snowflake-6" />
    {/* Star-shaped snowflakes */}
    <g className="snowflake-1" transform="translate(45, 5)">
      <line x1="0" y1="-3" x2="0" y2="3" stroke="#FFFFFF" strokeWidth="1" />
      <line x1="-3" y1="0" x2="3" y2="0" stroke="#FFFFFF" strokeWidth="1" />
      <line x1="-2" y1="-2" x2="2" y2="2" stroke="#FFFFFF" strokeWidth="0.5" />
      <line x1="-2" y1="2" x2="2" y2="-2" stroke="#FFFFFF" strokeWidth="0.5" />
    </g>
    <g className="snowflake-3" transform="translate(20, 0)">
      <line x1="0" y1="-2" x2="0" y2="2" stroke="#FFFFFF" strokeWidth="0.8" />
      <line x1="-2" y1="0" x2="2" y2="0" stroke="#FFFFFF" strokeWidth="0.8" />
    </g>
  </svg>
);

// ============================================
// EASTERN HILLS ASSETS (Stage 3)
// ============================================

export const Windmill = ({ size = 80 }) => (
  <svg width={size} height={size * 1.2} viewBox="0 0 80 96">
    <style>
      {`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .windmill-blades {
          transform-origin: 40px 30px;
          animation: spin 4s linear infinite;
        }
      `}
    </style>
    {/* Building */}
    <polygon points="30,96 50,96 55,40 25,40" fill="#D7CCC8" stroke="#BCAAA4" strokeWidth="1" />
    {/* Roof */}
    <polygon points="20,40 40,20 60,40" fill="#8D6E63" />
    {/* Door */}
    <rect x="35" y="75" width="10" height="21" fill="#5D4037" rx="5" />
    {/* Windows */}
    <rect x="32" y="50" width="6" height="8" fill="#81D4FA" />
    <rect x="42" y="50" width="6" height="8" fill="#81D4FA" />
    <rect x="37" y="62" width="6" height="8" fill="#81D4FA" />
    {/* Blades hub */}
    <circle cx="40" cy="30" r="5" fill="#795548" />
    {/* Animated Blades */}
    <g className="windmill-blades">
      <rect x="38" y="5" width="4" height="22" fill="#EFEBE9" stroke="#D7CCC8" rx="2" />
      <rect x="18" y="28" width="22" height="4" fill="#EFEBE9" stroke="#D7CCC8" rx="2" />
      <rect x="38" y="33" width="4" height="22" fill="#EFEBE9" stroke="#D7CCC8" rx="2" />
      <rect x="42" y="28" width="22" height="4" fill="#EFEBE9" stroke="#D7CCC8" rx="2" />
    </g>
  </svg>
);

export const FlowerField = ({ size = 50 }) => (
  <svg width={size} height={size * 0.6} viewBox="0 0 50 30">
    {/* Grass base */}
    <ellipse cx="25" cy="25" rx="24" ry="8" fill="#81C784" />
    {/* Flowers */}
    {[8, 18, 28, 38, 13, 23, 33].map((x, i) => (
      <g key={i}>
        <line x1={x} y1={25 - (i % 3) * 3} x2={x} y2={15 - (i % 3) * 3} stroke="#4CAF50" strokeWidth="1" />
        <circle cx={x} cy={13 - (i % 3) * 3} r="4" fill={['#FF6B6B', '#FFE66D', '#FF69B4', '#9B59B6', '#FF6B6B', '#FFE66D', '#FF69B4'][i]} />
        <circle cx={x} cy={13 - (i % 3) * 3} r="1.5" fill="#FFE082" />
      </g>
    ))}
  </svg>
);

export const RollingHill = ({ size = 100 }) => (
  <svg width={size} height={size * 0.4} viewBox="0 0 100 40">
    <path d="M0 40 Q25 10 50 30 Q75 10 100 40 Z" fill="#A5D6A7" />
    <path d="M0 40 Q30 20 60 35 Q80 25 100 40 Z" fill="#81C784" opacity="0.7" />
    {/* Small details */}
    <ellipse cx="20" cy="32" rx="3" ry="1.5" fill="#66BB6A" />
    <ellipse cx="70" cy="28" rx="4" ry="2" fill="#66BB6A" />
    <ellipse cx="45" cy="35" rx="3" ry="1.5" fill="#66BB6A" />
  </svg>
);

export const Butterfly = ({ size = 20 }) => (
  <svg width={size} height={size * 0.8} viewBox="0 0 20 16">
    <style>
      {`
        @keyframes flutter {
          0%, 100% { transform: scaleX(1); }
          50% { transform: scaleX(0.7); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        .butterfly-body {
          animation: float 2s ease-in-out infinite;
        }
        .butterfly-wing-left {
          transform-origin: 10px 8px;
          animation: flutter 0.3s ease-in-out infinite;
        }
        .butterfly-wing-right {
          transform-origin: 10px 8px;
          animation: flutter 0.3s ease-in-out infinite reverse;
        }
      `}
    </style>
    <g className="butterfly-body">
      {/* Wings */}
      <g className="butterfly-wing-left">
        <ellipse cx="6" cy="6" rx="5" ry="4" fill="#E91E63" opacity="0.8" />
        <ellipse cx="5" cy="11" rx="4" ry="3" fill="#F48FB1" opacity="0.8" />
        <circle cx="6" cy="5" r="1.5" fill="#FFFFFF" opacity="0.5" />
      </g>
      <g className="butterfly-wing-right">
        <ellipse cx="14" cy="6" rx="5" ry="4" fill="#E91E63" opacity="0.8" />
        <ellipse cx="15" cy="11" rx="4" ry="3" fill="#F48FB1" opacity="0.8" />
        <circle cx="14" cy="5" r="1.5" fill="#FFFFFF" opacity="0.5" />
      </g>
      {/* Body */}
      <ellipse cx="10" cy="8" rx="1.5" ry="6" fill="#4A148C" />
      {/* Antenna */}
      <path d="M9 2 Q7 0 6 1" fill="none" stroke="#4A148C" strokeWidth="0.5" />
      <path d="M11 2 Q13 0 14 1" fill="none" stroke="#4A148C" strokeWidth="0.5" />
    </g>
  </svg>
);

// ============================================
// WESTERN MOUNTAINS ASSETS (Stage 5)
// ============================================

export const MountainPeak = ({ size = 100 }) => (
  <svg width={size} height={size * 0.8} viewBox="0 0 100 80">
    {/* Back mountain */}
    <polygon points="20,80 50,20 80,80" fill="#78909C" />
    <polygon points="50,20 45,35 55,35" fill="#ECEFF1" />
    {/* Front mountain */}
    <polygon points="0,80 40,30 70,80" fill="#607D8B" />
    <polygon points="40,30 35,42 45,42" fill="#FFFFFF" />
    {/* Small peak */}
    <polygon points="60,80 85,45 100,80" fill="#546E7A" />
    <polygon points="85,45 82,52 88,52" fill="#ECEFF1" />
  </svg>
);

export const CaveEntrance = ({ size = 70 }) => (
  <svg width={size} height={size * 0.8} viewBox="0 0 70 56">
    {/* Rock formation */}
    <path d="M5 56 L10 30 Q20 10 35 5 Q50 10 60 30 L65 56 Z" fill="#795548" />
    {/* Cave opening */}
    <ellipse cx="35" cy="45" rx="18" ry="14" fill="#3E2723" />
    {/* Rock details */}
    <path d="M15 40 Q20 35 25 42" fill="none" stroke="#5D4037" strokeWidth="2" />
    <path d="M45 38 Q50 32 55 40" fill="none" stroke="#5D4037" strokeWidth="2" />
    {/* Stalactites */}
    <polygon points="28,31 30,40 32,31" fill="#4E342E" />
    <polygon points="38,33 40,43 42,33" fill="#4E342E" />
    {/* Moss/vegetation */}
    <ellipse cx="12" cy="35" rx="4" ry="2" fill="#558B2F" />
    <ellipse cx="58" cy="38" rx="3" ry="2" fill="#558B2F" />
  </svg>
);

export const RockyOutcrop = ({ size = 60 }) => (
  <svg width={size} height={size * 0.7} viewBox="0 0 60 42">
    {/* Main rocks */}
    <polygon points="10,42 5,25 15,15 25,20 20,42" fill="#78909C" />
    <polygon points="20,42 25,20 35,10 45,18 40,42" fill="#607D8B" />
    <polygon points="35,42 40,25 50,20 55,30 50,42" fill="#546E7A" />
    {/* Highlights */}
    <polygon points="15,18 18,22 12,22" fill="#90A4AE" />
    <polygon points="35,15 38,20 32,20" fill="#90A4AE" />
    <polygon points="48,23 51,28 46,28" fill="#78909C" />
    {/* Small rocks at base */}
    <ellipse cx="15" cy="40" rx="5" ry="3" fill="#455A64" />
    <ellipse cx="45" cy="41" rx="4" ry="2" fill="#455A64" />
  </svg>
);

export const CrystalFormation = ({ size = 45 }) => (
  <svg width={size} height={size} viewBox="0 0 45 45">
    {/* Purple crystals */}
    <polygon points="22,5 18,25 26,25" fill="#9C27B0" />
    <polygon points="22,5 20,20 24,20" fill="#BA68C8" />
    <polygon points="10,15 8,35 16,35" fill="#7B1FA2" />
    <polygon points="10,15 9,30 13,30" fill="#AB47BC" />
    <polygon points="35,12 30,38 40,38" fill="#8E24AA" />
    <polygon points="35,12 32,32 38,32" fill="#CE93D8" />
    {/* Small crystals */}
    <polygon points="5,28 3,40 8,40" fill="#6A1B9A" />
    <polygon points="42,25 40,40 45,40" fill="#6A1B9A" />
    {/* Base rocks */}
    <ellipse cx="22" cy="42" rx="20" ry="5" fill="#5D4037" />
  </svg>
);

export const MossyRock = ({ size = 40 }) => (
  <svg width={size} height={size * 0.7} viewBox="0 0 40 28">
    {/* Main rock */}
    <ellipse cx="20" cy="20" rx="18" ry="10" fill="#78909C" />
    <ellipse cx="18" cy="18" rx="14" ry="8" fill="#90A4AE" />
    {/* Moss patches */}
    <ellipse cx="10" cy="15" rx="6" ry="3" fill="#558B2F" />
    <ellipse cx="25" cy="12" rx="5" ry="3" fill="#689F38" />
    <ellipse cx="30" cy="18" rx="4" ry="2" fill="#7CB342" />
    {/* Small moss details */}
    <circle cx="8" cy="18" r="2" fill="#8BC34A" />
    <circle cx="28" cy="10" r="1.5" fill="#9CCC65" />
  </svg>
);

// Export map for easy lookup by type
export const BIOME_ASSET_MAP = {
  // Center Town (Stage 1)
  townFlower: TownFlower,
  parkBench: ParkBench,
  fountain: Fountain,
  lamppost: Lamppost,
  signpost: Signpost,
  picnicTable: PicnicTable,
  mushroom: Mushroom,
  birdhouse: Birdhouse,
  // Southern Beach
  palmTree: PalmTree,
  sandcastle: Sandcastle,
  beachUmbrella: BeachUmbrella,
  seashell: Seashell,
  beachBall: BeachBall,
  crab: Crab,
  // Snowy North
  snowman: Snowman,
  snowyPine: SnowyPine,
  iceCrystal: IceCrystal,
  igloo: Igloo,
  snowPile: SnowPile,
  fallingSnow: FallingSnow,
  // Eastern Hills
  windmill: Windmill,
  flowerField: FlowerField,
  rollingHill: RollingHill,
  butterfly: Butterfly,
  // Western Mountains
  mountainPeak: MountainPeak,
  caveEntrance: CaveEntrance,
  rockyOutcrop: RockyOutcrop,
  crystalFormation: CrystalFormation,
  mossyRock: MossyRock,
};
